import React, { PropsWithChildren } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CollectionTags from '@components/myScores/ScoresView/CollectionTags'
import PublishedScoreStatus from '@components/myScores/ScoresView/PublishedScoreStatus'
import ScoreTitle from '@components/myScores/ScoresView/ScoreTitle'
import ScoreTypeLabel from '@components/myScores/ScoresView/ScoreTypeLabel'
import SocialIcon from '@components/myScores/SocialIcon'
import { ScoreGridOptions } from '@components/myScores/utils/types'
import NFIcon from '@components/shared/NFIcon'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Product from '@models/Product'
import Score from '@models/Score'
import { isProduct } from '@models/util/product.util'
import { isScore } from '@models/util/score.util'
import { useSelection } from '@providers/SelectionProvider'
import { formatDate } from '@utils/formatters/date.formatter'
import { isEmpty } from '@utils/helpers'
import PageRoutes from '@utils/page-routes'

import CoverArtHandle from './CoverArtHandle'

interface Props {
  score: Score | Product
  options?: ScoreGridOptions
}

const ScoreGridItem: React.FC<Props> = ({ score, options }: Props) => {
  const { isSorting } = useSelection()
  const sortable = useSortable({ id: score.id })
  const { t } = useTranslation()
  const navigate = useNavigate()

  const onClick = () => {
    let shouldNavigate = true
    if (typeof options?.beforeNavigate === 'function') {
      shouldNavigate = options.beforeNavigate(score)
    }

    if (shouldNavigate) {
      if (isProduct(score)) {
        if (score.music_url) {
          navigate(score.music_url)
        } else {
          window.location.href = score.marketplace_url
        }
      } else {
        window.location.href = PageRoutes.editorPath(score.external_id)
      }
    }
  }

  return (
    <SortWrapper canSort={options?.sortable} sortable={sortable}>
      <div
        key={`score-${score.id}`}
        title={score.title ?? score.external_id}
        className={classNames(
          'bg-white dark:bg-black-bg2 p-2 flex flex-row gap-4 min-h-[68px] min-w-0 rounded-sm',
          {
            'opacity-75': sortable.isSorting && !sortable.isDragging,
            'cursor-grab': isSorting && !sortable.isDragging,
          },
          sortable.isDragging
            ? 'shadow-lg z-10 cursor-grabbing'
            : 'hover:bg-opacity-60 dark:hover:bg-opacity-60'
        )}>
        <CoverArtHandle score={score} editable={!options?.readonly} />
        <button
          role="link"
          onClick={onClick}
          disabled={isSorting}
          className="flex flex-row gap-1 flex-1 text-left text-base min-w-0">
          {/* Left Column */}
          <div className="flex flex-col flex-1 min-w-0 gap-1 items-start justify-center ellipsify">
            {/* Score Title */}
            <div className="flex-1 text-base leading-[0.8125rem] font-semibold ellipsify h-4 w-full">
              <ScoreTitle score={score} />

              {/* Author */}
              {score.author?.username && options?.showAuthor && (
                <>
                  <span className="text-gray-text dark:text-gray-text-dm mx-1 font-normal">
                    {t('content.authorPrefix')}
                  </span>
                  <a
                    href={PageRoutes.userProfilePath(score.author.external_id)}
                    title={score.author.username}
                    className="orange-link font-normal">
                    {score.author.username}
                  </a>
                </>
              )}

              {/* Official badge (product only) */}
              {isProduct(score) && score.official && (
                <NFIcon icon="original" className="ml-2 text-orange text-md" />
              )}
            </div>

            {/* Composer (score only) */}
            {isScore(score) && score.composer && (
              <div className="flex-1 ellipsify w-full">
                <span title={score.composer}>{score.composer}</span>
              </div>
            )}

            {/* Social */}
            {!options?.hideSocial && hasSocial(score) && (
              <div className="flex flex-row items-center gap-1.5 h-4">
                {!!score.favorites_count && (
                  <SocialIcon type="favorites" count={score.favorites_count} />
                )}
                {score.views_count > 0 && <SocialIcon type="views" count={score.views_count} />}

                {/* Comments (score only) */}
                {isScore(score) && score.comments_count > 0 && (
                  <SocialIcon type="comments" count={score.comments_count} />
                )}
              </div>
            )}

            {/* Collections */}
            {!options?.hideCollections && !isEmpty(score.collections) && (
              <div className="flex-1 h-4">
                <CollectionTags collections={score.collections} numShown={3} />
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="flex flex-col items-end justify-center gap-1">
            {/* Updated */}
            {!options?.hideTimestamp && (
              <p className="text-sm text-gray-text dark:text-gray-text-dm h-4">
                {formatDate(score.updated)}
              </p>
            )}

            {/* Type (score only) */}
            {isScore(score) && !options?.hideState && (
              <ScoreTypeLabel score={score} className="h-4" />
            )}

            {/* Published Score (score only) */}
            {isScore(score) && <PublishedScoreStatus score={score} className="h-4" />}
          </div>
        </button>
      </div>
    </SortWrapper>
  )
}

export default ScoreGridItem

function hasSocial(score: Score | Product): boolean {
  if (isScore(score)) {
    return !!score.views_count || !!score.comments_count || !!score.favorites_count
  }
  return !!score.views_count || !!score.favorites_count
}

interface SortProps {
  sortable: ReturnType<typeof useSortable>
  canSort?: boolean
}
const SortWrapper: React.FC<PropsWithChildren<SortProps>> = ({ children, sortable, canSort }) => {
  const { isSorting } = useSelection()

  if (isSorting && canSort) {
    return (
      <div
        ref={sortable.setNodeRef}
        style={{
          transform: CSS.Transform.toString(sortable.transform),
          transition: sortable.transition,
        }}
        {...sortable.listeners}
        {...sortable.attributes}>
        {children}
      </div>
    )
  }
  return <>{children}</>
}
